/**
 * PDF Analysis Service
 * Analyzes uploaded marksheet PDFs to extract academic data and provide insights
 */

import { getFileDownloadURL } from '../firebase/r2storage';

/**
 * Analyzes marksheet PDFs and extracts academic insights
 * @param {Object} profileSettings - User's profile settings containing document URLs
 * @returns {Promise<Object>} Analysis results with extracted data and insights
 */
export async function analyzeMarksheetPDFs(profileSettings) {
  if (!profileSettings?.profile?.results) {
    return {
      hasDocuments: false,
      class10Analysis: null,
      class12Analysis: null,
      insights: [],
      recommendations: []
    };
  }

  const results = profileSettings.profile.results;
  const analysis = {
    hasDocuments: false,
    class10Analysis: null,
    class12Analysis: null,
    insights: [],
    recommendations: [],
    subjectWisePerformance: []
  };

  // Analyze Class 10 document
  if (results.class10?.documentUrl) {
    analysis.hasDocuments = true;
    analysis.class10Analysis = await analyzeMarksheet(
      results.class10.documentUrl,
      'Class 10',
      results.class10
    );
  }

  // Analyze Class 12 document
  if (results.class12?.documentUrl) {
    analysis.hasDocuments = true;
    analysis.class12Analysis = await analyzeMarksheet(
      results.class12.documentUrl,
      'Class 12',
      results.class12
    );
  }

  // Generate comprehensive insights
  if (analysis.class10Analysis || analysis.class12Analysis) {
    analysis.insights = generateInsights(analysis.class10Analysis, analysis.class12Analysis);
    analysis.recommendations = generateRecommendations(analysis.class10Analysis, analysis.class12Analysis);
    analysis.subjectWisePerformance = extractSubjectPerformance(analysis.class10Analysis, analysis.class12Analysis);
  }

  return analysis;
}

/**
 * Analyzes a single marksheet document
 * @param {string} documentUrl - Firebase Storage URL of the document
 * @param {string} className - Class name (e.g., 'Class 10', 'Class 12')
 * @param {Object} manualData - Manually entered data from profile
 * @returns {Promise<Object>} Analysis of the marksheet
 */
async function analyzeMarksheet(documentUrl, className, manualData) {
  try {
    const analysis = {
      className,
      documentUrl,
      hasDocument: true,
      percentage: parseFloat(manualData.percentage) || 0,
      board: manualData.board || 'Not specified',
      year: manualData.year || 'Not specified',
      school: manualData.school || 'Not specified',
      grade: calculateGrade(parseFloat(manualData.percentage) || 0),
      performanceLevel: getPerformanceLevel(parseFloat(manualData.percentage) || 0),
      subjects: extractSubjectsFromBoard(manualData.board),
      insights: []
    };

    // Generate insights based on percentage
    if (analysis.percentage >= 90) {
      analysis.insights.push('Outstanding performance! You are in the top tier of students.');
      analysis.insights.push('Your strong foundation makes you eligible for premier institutions.');
    } else if (analysis.percentage >= 75) {
      analysis.insights.push('Excellent performance with a solid academic foundation.');
      analysis.insights.push('You have strong potential for competitive examinations.');
    } else if (analysis.percentage >= 60) {
      analysis.insights.push('Good performance showing consistent effort.');
      analysis.insights.push('Focus on strengthening core concepts for better results.');
    } else if (analysis.percentage >= 50) {
      analysis.insights.push('Average performance with room for improvement.');
      analysis.insights.push('Targeted practice can significantly boost your scores.');
    } else if (analysis.percentage > 0) {
      analysis.insights.push('There is significant scope for improvement.');
      analysis.insights.push('Consider focused study plans and additional support.');
    }

    return analysis;
  } catch (error) {
    console.error(`Error analyzing ${className} marksheet:`, error);
    return null;
  }
}

/**
 * Calculates grade based on percentage
 */
function calculateGrade(percentage) {
  if (percentage >= 90) return 'A+';
  if (percentage >= 80) return 'A';
  if (percentage >= 70) return 'B+';
  if (percentage >= 60) return 'B';
  if (percentage >= 50) return 'C';
  if (percentage >= 40) return 'D';
  return 'F';
}

/**
 * Gets performance level description
 */
function getPerformanceLevel(percentage) {
  if (percentage >= 90) return 'Outstanding';
  if (percentage >= 75) return 'Excellent';
  if (percentage >= 60) return 'Good';
  if (percentage >= 50) return 'Average';
  if (percentage >= 40) return 'Below Average';
  return 'Needs Improvement';
}

/**
 * Extracts likely subjects based on board/stream
 */
function extractSubjectsFromBoard(board) {
  if (!board) return [];
  
  const boardLower = board.toLowerCase();
  
  if (boardLower.includes('pcm') || boardLower.includes('science') && boardLower.includes('math')) {
    return ['Physics', 'Chemistry', 'Mathematics', 'English'];
  } else if (boardLower.includes('pcb') || boardLower.includes('biology')) {
    return ['Physics', 'Chemistry', 'Biology', 'English'];
  } else if (boardLower.includes('commerce')) {
    return ['Accountancy', 'Business Studies', 'Economics', 'English'];
  } else if (boardLower.includes('arts') || boardLower.includes('humanities')) {
    return ['History', 'Political Science', 'English', 'Optional Subject'];
  }
  
  return ['Core Subjects'];
}

/**
 * Generates comprehensive insights from both marksheets
 */
function generateInsights(class10Analysis, class12Analysis) {
  const insights = [];

  if (class10Analysis && class12Analysis) {
    const improvement = class12Analysis.percentage - class10Analysis.percentage;
    
    if (improvement > 10) {
      insights.push({
        type: 'positive',
        title: 'Significant Improvement',
        message: `You improved by ${improvement.toFixed(1)}% from Class 10 to Class 12. This shows excellent growth and dedication!`
      });
    } else if (improvement > 5) {
      insights.push({
        type: 'positive',
        title: 'Steady Progress',
        message: `You improved by ${improvement.toFixed(1)}% from Class 10 to Class 12. Keep up the good work!`
      });
    } else if (improvement < -5) {
      insights.push({
        type: 'warning',
        title: 'Performance Dip',
        message: `Your score decreased by ${Math.abs(improvement).toFixed(1)}% from Class 10 to Class 12. Consider identifying and addressing the challenges.`
      });
    } else {
      insights.push({
        type: 'neutral',
        title: 'Consistent Performance',
        message: 'You maintained consistent performance across both classes.'
      });
    }

    // Average performance insight
    const avgPercentage = (class10Analysis.percentage + class12Analysis.percentage) / 2;
    insights.push({
      type: 'info',
      title: 'Overall Academic Strength',
      message: `Your average academic performance is ${avgPercentage.toFixed(1)}%, placing you in the ${getPerformanceLevel(avgPercentage)} category.`
    });
  }

  // Individual class insights
  if (class10Analysis) {
    insights.push({
      type: 'info',
      title: 'Class 10 Foundation',
      message: `${class10Analysis.board} - ${class10Analysis.percentage}% (${class10Analysis.grade}). ${class10Analysis.insights[0] || ''}`
    });
  }

  if (class12Analysis) {
    insights.push({
      type: 'info',
      title: 'Class 12 Specialization',
      message: `${class12Analysis.board} - ${class12Analysis.percentage}% (${class12Analysis.grade}). Stream: ${class12Analysis.subjects.join(', ')}`
    });
  }

  return insights;
}

/**
 * Generates personalized recommendations based on marksheet analysis
 */
function generateRecommendations(class10Analysis, class12Analysis) {
  const recommendations = [];

  const latestPercentage = class12Analysis?.percentage || class10Analysis?.percentage || 0;
  const stream = class12Analysis?.board?.toLowerCase() || '';

  // Study recommendations based on performance
  if (latestPercentage >= 85) {
    recommendations.push({
      category: 'Study Strategy',
      title: 'Maintain Excellence',
      description: 'Continue your current study methods. Focus on advanced problem-solving and competitive exam preparation.',
      priority: 'high'
    });
  } else if (latestPercentage >= 70) {
    recommendations.push({
      category: 'Study Strategy',
      title: 'Push for Excellence',
      description: 'You have a strong foundation. Focus on weak areas and practice more challenging problems to reach the top tier.',
      priority: 'medium'
    });
  } else if (latestPercentage >= 50) {
    recommendations.push({
      category: 'Study Strategy',
      title: 'Strengthen Fundamentals',
      description: 'Focus on building strong conceptual understanding. Regular practice and doubt clearing sessions will help.',
      priority: 'high'
    });
  } else {
    recommendations.push({
      category: 'Study Strategy',
      title: 'Intensive Preparation Needed',
      description: 'Consider personalized coaching, regular assessments, and structured study plans to improve performance.',
      priority: 'critical'
    });
  }

  // Stream-specific recommendations
  if (stream.includes('pcm') || stream.includes('science')) {
    recommendations.push({
      category: 'Career Path',
      title: 'Engineering & Technology',
      description: 'Your science background opens doors to JEE, BITSAT, and various engineering entrance exams.',
      priority: 'high'
    });
  } else if (stream.includes('pcb') || stream.includes('biology')) {
    recommendations.push({
      category: 'Career Path',
      title: 'Medical & Life Sciences',
      description: 'Consider NEET preparation for medical courses or explore biotechnology and life sciences options.',
      priority: 'high'
    });
  } else if (stream.includes('commerce')) {
    recommendations.push({
      category: 'Career Path',
      title: 'Commerce & Management',
      description: 'Explore CA, CS, CMA, or prepare for management entrance exams like CAT, CLAT.',
      priority: 'high'
    });
  }

  // Document verification recommendation
  if (class10Analysis?.hasDocument || class12Analysis?.hasDocument) {
    recommendations.push({
      category: 'Documentation',
      title: 'Documents Uploaded',
      description: 'Your marksheets are securely stored. Keep them updated for admission and verification purposes.',
      priority: 'low'
    });
  } else {
    recommendations.push({
      category: 'Documentation',
      title: 'Upload Marksheets',
      description: 'Upload your Class 10 and 12 marksheets for comprehensive analysis and future reference.',
      priority: 'medium'
    });
  }

  return recommendations;
}

/**
 * Extracts subject-wise performance data
 */
function extractSubjectPerformance(class10Analysis, class12Analysis) {
  const performance = [];

  if (class10Analysis) {
    performance.push({
      class: 'Class 10',
      subjects: class10Analysis.subjects,
      overallPercentage: class10Analysis.percentage,
      grade: class10Analysis.grade
    });
  }

  if (class12Analysis) {
    performance.push({
      class: 'Class 12',
      subjects: class12Analysis.subjects,
      overallPercentage: class12Analysis.percentage,
      grade: class12Analysis.grade
    });
  }

  return performance;
}

/**
 * Validates if a document URL is accessible
 */
export async function validateDocumentUrl(documentUrl) {
  try {
    if (!documentUrl) return false;
    
    // Check if it's a valid Firebase Storage URL
    if (documentUrl.includes('firebasestorage.googleapis.com')) {
      return true;
    }
    
    return false;
  } catch (error) {
    console.error('Error validating document URL:', error);
    return false;
  }
}

export default {
  analyzeMarksheetPDFs,
  validateDocumentUrl
};
